import type { QueryClient } from '@tanstack/react-query';

/**
 * How a feature follows its own hints on the user's inbox channel.
 *
 * The server sends a feature's events on the same channel as the inbox's,
 * with types named `<prefix>.<event>`, so a feature needs no socket of its
 * own. A hint names what changed and never what it says: `apply` decides
 * which caches to re-read, and `onResume` re-reads whatever a missed hint
 * could have left stale, because the channel keeps no history.
 *
 * A feature exports its handler from its `/realtime` entry and is listed in
 * `FEATURE_REALTIME_HANDLERS`; `useInboxLiveUpdates` does the rest.
 */
export interface FeatureRealtimeHandler<TEvent = unknown> {
  /** The type prefix the server registered for the feature, without the dot. */
  prefix: string;
  /** Throws on anything that is not a known event of the feature, which drops it. */
  parse(raw: unknown): TEvent;
  apply(queryClient: QueryClient, event: TEvent): void;
  onResume?(queryClient: QueryClient): void;
}

/**
 * Declares a handler with its event type inferred from `parse`, so `apply`
 * is checked against what `parse` returns.
 */
export function defineFeatureRealtimeHandler<TEvent>(
  handler: FeatureRealtimeHandler<TEvent>
): FeatureRealtimeHandler<TEvent> {
  return handler;
}

/** One event on the inbox channel, after routing. */
export type RoutedRealtimeEvent<TInbox> =
  | { kind: 'inbox'; event: TInbox }
  | { kind: 'feature'; handler: FeatureRealtimeHandler; event: unknown };

function eventType(raw: unknown): string | undefined {
  if (typeof raw !== 'object' || raw === null || !('type' in raw)) {
    return undefined;
  }

  return typeof raw.type === 'string' ? raw.type : undefined;
}

/**
 * Routes a raw event to the feature whose prefix its type carries, or to the
 * inbox parser otherwise. Either parser throws on a type it does not know,
 * so an event from a feature this build has never heard of is dropped by the
 * inbox parser, the same as an unknown inbox event.
 */
export function routeRealtimeEvent<TInbox>(
  raw: unknown,
  handlers: readonly FeatureRealtimeHandler[],
  parseInbox: (raw: unknown) => TInbox
): RoutedRealtimeEvent<TInbox> {
  const type = eventType(raw);

  const handler = type
    ? handlers.find((candidate) => type.startsWith(`${candidate.prefix}.`))
    : undefined;

  if (handler) {
    return { kind: 'feature', handler, event: handler.parse(raw) };
  }

  return { kind: 'inbox', event: parseInbox(raw) };
}
